import { useState } from 'react';
import styled from '@emotion/styled';
import { Season } from '../../apiService';
import { IconDown } from '../../icons';
import Menu from '../../Menu';
import { MenuItem, RotatableIcon } from '../../styles';
import { getEpisodeIdentifier } from '../../utils';

type SeasonSelectorProps = {
  seasons: Season[];
  active: number;
  onChange: (index: number) => void;
};

/** A season goes by its own title here; only one the API left untitled falls back to a number. */
const seasonLabel = ({ season = 0, season_number, season_title }: Season) => (
  season_title || getEpisodeIdentifier({ season: season_number ?? season })
);

/**
 * The season picker for shorts. Separate from the series one because it is named rather than
 * numbered, and so has to be sized by its label instead of by a fixed box.
 */
export default function SeasonSelector({ seasons, active, onChange }: SeasonSelectorProps) {
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);

  if (seasons.length < 2) return null;

  const select = (index: number) => {
    onChange(index);
    setOpen(false);
  };

  return (
    <>
      <Anchor as="button" type="button" ref={setAnchor} onClick={() => setOpen((prev) => !prev)}>
        <Label>{seasonLabel(seasons[active])}</Label>
        <RotatableIcon trigger={open} cursor="pointer"><IconDown /></RotatableIcon>
      </Anchor>

      {/* The list sizes itself to its labels while the button carries the chevron too, so
          without a floor it opens narrower than the control it belongs to. Measured on the
          render that opens the menu, by which point the button is long since laid out. */}
      <SeasonMenu
        as="ul"
        px={0}
        py={0}
        offsetY={1}
        showArrow={false}
        open={open}
        anchor={anchor}
        onClose={() => setOpen(false)}
        style={{ minWidth: anchor?.offsetWidth }}
      >
        {seasons.map((season, index) => (
          <MenuItem key={season.id || index} isActive={index === active} onClick={() => select(index)}>
            {seasonLabel(season)}
          </MenuItem>
        ))}
      </SeasonMenu>
    </>
  );
}

// Sized by its label: it takes exactly the width the season name needs, and gives that width
// back to whatever shares its row once the name is long enough to have to be cut short.
const Anchor = styled(MenuItem)(({ theme }) => ({
  flex: '0 1 auto',
  minWidth: 0,
  maxWidth: '100%',
  gap: theme.spacing(1),
  borderRadius: theme.radius.medium,
  [theme.logicalProp('margin', 'start')]: theme.spacing(2),
  [`${RotatableIcon}`]: {
    flexShrink: 0,
  },
}));

const Label = styled.span({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const SeasonMenu = styled(Menu)({
  maxHeight: '30vh',
  maxWidth: '80vw',
  overflowY: 'auto',
});
